/**
 * escalation-api.ts — HTTP client for the escalation queue admin endpoints.
 *
 * Story 8.2: Task 7.3
 *
 * All endpoints route through the REST gateway's per-service admin group
 * (`/v1/integrations/admin` -> `/admin`, Epic 12.3) to betty-integrations. Gateway
 * strips `/v1/integrations`; service sees `/admin/escalations/*` (EscalationQueueController
 * remounted from `v1/admin/escalations` in the same review-fix — see that controller's
 * header comment for why).
 *
 * PHI: escalation data is PHI-derived — no logging of member identifiers.
 *
 * Auth: staff JWT attached via Authorization header (Cognito OIDC).
 */

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type EscalationSeverity = 'low' | 'medium' | 'high' | 'emergency';
export type EscalationStatus = 'open' | 'claimed' | 'resolved';
export type EscalationTriggerSource = 'rules_engine' | 'wa_escalated';
export type EscalationTriggerType =
  | 'threshold'
  | 'delta'
  | 'behavioral'
  | 'sdoh'
  | 'cluster'
  | 'keyword';

export interface EscalationClaimedBy {
  staffId: string;
  staffRole: string;
  claimedAt: string;
}

export interface EscalationQueueEntry {
  id: string;
  memberId: string;
  triggerSource: EscalationTriggerSource;
  triggerType: EscalationTriggerType;
  severity: EscalationSeverity;
  triggerTimestamp: string;
  slaDeadlineAt: string;
  slaRemainingSeconds: number;
  status: EscalationStatus;
  claimedBy: EscalationClaimedBy | null;
  resolvedBy: {
    staffId: string;
    staffRole: string;
    resolvedAt: string;
    resolutionNote: string;
  } | null;
  memberName: string | null;
  triggerReason: string | null;
  conversationSnippet: string | null;
  memberBackground: string | null;
  conversationHistory?: string | null;
  provenance: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EscalationQueueListResponse {
  items: EscalationQueueEntry[];
  nextCursor: string | null;
  totalCount: number;
}

export interface EscalationListParams {
  status?: EscalationStatus;
  severity?: EscalationSeverity;
  cursor?: string;
  limit?: number;
}

// ─── API helper ───────────────────────────────────────────────────────────────

async function apiFetch(path: string, options?: RequestInit, token?: string): Promise<Response> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers ?? {}),
  };
  return fetch(`${SERVICES_BASE_URL}/v1/integrations${path}`, {
    ...options,
    headers,
  });
}

async function parseRfc9457Error(response: Response): Promise<Error> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/problem+json')) {
    try {
      const problem = (await response.json()) as { title?: string; detail?: string };
      return new Error(problem.detail ?? problem.title ?? `HTTP ${String(response.status)}`);
    } catch {
      // fall through
    }
  }
  return new Error(`Request failed with status ${String(response.status)}`);
}

// ─── API functions ────────────────────────────────────────────────────────────

export const escalationApi = {
  async listEscalations(
    params: EscalationListParams = {},
    token?: string,
  ): Promise<EscalationQueueListResponse> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.severity) query.set('severity', params.severity);
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));

    const qs = query.toString();
    const response = await apiFetch(
      `/admin/escalations${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as EscalationQueueListResponse;
  },

  async getEscalation(escalationId: string, token?: string): Promise<EscalationQueueEntry> {
    const response = await apiFetch(`/admin/escalations/${escalationId}`, { method: 'GET' }, token);
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as EscalationQueueEntry;
  },

  async claimEscalation(
    escalationId: string,
    idempotencyKey: string,
    token?: string,
  ): Promise<EscalationQueueEntry> {
    const response = await apiFetch(
      `/admin/escalations/${escalationId}/claim`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as EscalationQueueEntry;
  },

  async resolveEscalation(
    escalationId: string,
    resolutionNote: string,
    idempotencyKey: string,
    token?: string,
  ): Promise<EscalationQueueEntry> {
    const response = await apiFetch(
      `/admin/escalations/${escalationId}/resolve`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ resolutionNote }),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as EscalationQueueEntry;
  },
};
